import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';

/**
 * What the screen does once an allocation has been archived (LB-5952, LB-5953): by its uploaded bill being deleted,
 * or by being removed.
 */

/**
 * Whether apply billing is still open on the property, cycle and utility an archive was asked from. The property too:
 * in all-properties mode the modal can close and open on another property's table for the same cycle and utility.
 * Compared only when both name one -- a screen opened on one property need not say which.
 */
export const isOnUtility = (billingTab, from) => {
  const applyBill = billingTab === null || billingTab === void 0 ? void 0 : billingTab.applyBill;
  const isSameProperty = !(applyBill !== null && applyBill !== void 0 && applyBill.propertyId) || !(from !== null && from !== void 0 && from.propertyId) || applyBill.propertyId === from.propertyId;
  return !!(billingTab !== null && billingTab !== void 0 && billingTab.isApplyBillingIsOpen) && isSameProperty && (applyBill === null || applyBill === void 0 ? void 0 : applyBill.billingCycleId) === (from === null || from === void 0 ? void 0 : from.billingCycleId) && (applyBill === null || applyBill === void 0 ? void 0 : applyBill.utilityId) === (from === null || from === void 0 ? void 0 : from.utilityId);
};

/** Tries at reloading the table before the screen is closed rather than left showing what is gone. */
export const RELOAD_ATTEMPTS = 2;

/** A reloaded table's status, from its own flags when it came back without one (a table with no charges). */
export const reloadedStatus = reloaded => {
  if (reloaded !== null && reloaded !== void 0 && reloaded.billStatus) return reloaded.billStatus;
  if (reloaded !== null && reloaded !== void 0 && reloaded.isSkipped) return 'TableSkipped';
  if (reloaded !== null && reloaded !== void 0 && reloaded.isApproved) return 'TableReady';
  return 'PendingCompletion';
};

/**
 * The dashboard's summary reloads wherever the screen is now; the allocation only while the screen is still on it.
 * Its load is the one every utility shares, and starting it would cancel the load of a utility the person has moved
 * to, which loads its own table anyway. Then the table's status as reloaded, for the header and everything it gates:
 * an archived allocation is pending again.
 *
 * A reload that fails is tried again, and then the screen is closed: left open, it would go on showing the archived
 * allocation's charges, still approvable. One cancelled by a newer load of the table is left to that load.
 *
 * @param from the cycle, property and utility the archive was asked from
 * @returns `reloaded`, `moved` (the screen is elsewhere), `superseded` (a newer load took its place) or `closed`
 */
export async function refreshArchivedAllocation(_ref) {
  var _getBillingTab, _getBillingTab$update, _from$propertyId, _property$allocations2, _property$allocations3, _billingTab$setIsBill;
  let store = _ref.store,
    property = _ref.property,
    from = _ref.from;
  const getBillingTab = () => {
    var _store$homeDashboardS;
    return (_store$homeDashboardS = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.billingTab;
  };
  const reload = store.allocations.getAllocationTableAggregateAsync;
  (_getBillingTab = getBillingTab()) === null || _getBillingTab === void 0 || (_getBillingTab$update = _getBillingTab.updateBillingSummaryTimestamp) === null || _getBillingTab$update === void 0 || _getBillingTab$update.call(_getBillingTab);
  // The dashboard row of the table too -- its status, bill and allocated amounts -- wherever the screen is now. Left
  // as it was, Prev/Next and the row's Preview reopened the table as if it still had its bill, approvable with none.
  const propertyId = (_from$propertyId = from === null || from === void 0 ? void 0 : from.propertyId) !== null && _from$propertyId !== void 0 ? _from$propertyId : property === null || property === void 0 ? void 0 : property.id;
  if (propertyId && from !== null && from !== void 0 && from.billingCycleId) {
    var _getBillingTab2, _getBillingTab2$addTo, _store$homeDashboardS2;
    (_getBillingTab2 = getBillingTab()) === null || _getBillingTab2 === void 0 || (_getBillingTab2$addTo = _getBillingTab2.addToPreviewQueue) === null || _getBillingTab2$addTo === void 0 || _getBillingTab2$addTo.call(_getBillingTab2, {
      billingCycleId: from.billingCycleId,
      id: from.billingCycleId + propertyId,
      portfolioId: ((_store$homeDashboardS2 = store.homeDashboardStore.getCurrentPortfolio) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.id) || '',
      propertyId
    });
  }
  for (let attempt = 0; attempt < RELOAD_ATTEMPTS; attempt += 1) {
    if (!isOnUtility(getBillingTab(), from)) return 'moved';

    // eslint-disable-next-line no-await-in-loop
    const resp = await reload.run(from);
    if (resp) {
      var _property$allocations;
      const billingTab = getBillingTab();
      if (!isOnUtility(billingTab, from)) return 'moved';

      // A table with no charges comes back without a status: property-service builds it from the table row alone and
      // sets none. Its own flags still say where it stands -- an archive clears approval, so that is pending, unless the
      // table was skipped since. Never "as it was": kept, the header went on saying "To approve" over an empty,
      // approvable table.
      const reloaded = property === null || property === void 0 || (_property$allocations = property.allocations) === null || _property$allocations === void 0 || (_property$allocations = _property$allocations.utilityById) === null || _property$allocations === void 0 ? void 0 : _property$allocations.get(from.billingCycleId + from.utilityId);
      billingTab.setApplyBill(_objectSpread(_objectSpread({}, billingTab.applyBill), {}, {
        status: reloadedStatus(reloaded)
      }));
      return 'reloaded';
    }

    // Resolved with nothing and no error recorded: cancelled, by a newer load that is what the screen shows.
    if (!reload.error) return 'superseded';
  }
  const billingTab = getBillingTab();
  if (!isOnUtility(billingTab, from)) return 'moved';
  toast('error', {
    title: 'Table closed',
    body: ['The allocation was archived, but this table could not be reloaded. Open it again to see where it stands.']
  });
  property === null || property === void 0 || (_property$allocations2 = property.allocations) === null || _property$allocations2 === void 0 || (_property$allocations3 = _property$allocations2.resetTempFileData) === null || _property$allocations3 === void 0 || _property$allocations3.call(_property$allocations2);
  (_billingTab$setIsBill = billingTab.setIsBillEdit) === null || _billingTab$setIsBill === void 0 || _billingTab$setIsBill.call(billingTab, false);
  billingTab.setIsApplyBillingIsOpen(false);
  return 'closed';
}